import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Column,
  useTable,
  useGlobalFilter,
  useFilters,
  TableInstance,
  HeaderGroup,
  ColumnInstance,
  UseSortByColumnProps,
  useSortBy,
} from 'react-table';
import './RecommendationTableWidget.scss';
import { FrequencyChangeData } from '../../../models/RecommendationsModels';
import { DefaultColumnFilter } from '../../../components/data-tables/helpers/DefaultColumnFilter';
import { createMultiConditionFilter } from '../../../utilities/tableFilters';
import Helpers from '../../../utilities/Helpers';

interface RecommendationsTableProps {
  data: FrequencyChangeData[];
  columns: Column<FrequencyChangeData>[];
  searchTerm?: string;
  onRowClick?: (row: FrequencyChangeData) => void;
  widgetHeader?: string;
}

const RecommendationsTable: React.FC<RecommendationsTableProps> = ({
  data = [],
  columns,
  searchTerm = '',
  onRowClick,
  widgetHeader = '',
}) => {
  const tableData = useMemo(() => data || [], [data]);
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);
  const currentFiltersRef = useRef(currentFilters);

  // Helper function to calculate totals for specific fields
  const calculateTotal = (fieldName: string) => {
    const total = tableData.reduce((sum: number, item: any) => sum + (Number(item[fieldName]) || 0), 0);
    return Math.round(total).toString();
  };

  // Calculate totals based on widget header
  const totals = useMemo(() => {
    const totalsData: { [key: string]: string } = {};
    const header = (widgetHeader || '').toLowerCase();

    if (header === 'frequency change') {
      totalsData['FrequencyChangeAdditionalOil'] = calculateTotal('FrequencyChangeAdditionalOil');
      totalsData['FrequencyChangeAdditionalWater'] = calculateTotal('FrequencyChangeAdditionalWater');
      totalsData['IncrementalGain'] = calculateTotal('IncrementalGain');
    } else if (header === 'tracked frequency changes' || header === 'tracked injected gas rate changes') {
      totalsData['CumulativeGain'] = calculateTotal('CumulativeGain');
    } else if (header === 'pump change') {
      totalsData['AdditionalOil'] = calculateTotal('AdditionalOil');
    } else if (header === 'injection rate increase') {
      totalsData['AdditionalOilInjectedGasRateRequired'] = calculateTotal('AdditionalOilInjectedGasRateRequired');
      totalsData['AdditionalOilInjectedGasRateIncrease'] = calculateTotal('AdditionalOilInjectedGasRateIncrease');
      totalsData['IncrementalChange'] = calculateTotal('IncrementalChange');
      totalsData['NewGrossRate'] = calculateTotal('NewGrossRate');
    } else if (header === 'injection rate reduction') {
      totalsData['ReducedOilInjectedGasRateRequired'] = calculateTotal('ReducedOilInjectedGasRateRequired');
      totalsData['ReducedOilInjectedGasRateDecrease'] = calculateTotal('ReducedOilInjectedGasRateDecrease');
      totalsData['NewGrossRateReduced'] = calculateTotal('NewGrossRateReduced');
    } else if (header === 'whp reduction') {
      totalsData['AdditionalOilWHPDecrease'] = calculateTotal('AdditionalOilWHPDecrease');
      totalsData['NewGrossRateAtReducedWHP'] = calculateTotal('NewGrossRateAtReducedWHP');
    } else if (header === 'depth change') {
      totalsData['AdditionalOilDepthIncrease'] = calculateTotal('AdditionalOilDepthIncrease');
      totalsData['NewGrossRate'] = calculateTotal('NewGrossRate');
    }

    return totalsData;
  }, [tableData, widgetHeader]);

  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);

  const multiConditionFilter = useMemo(() => createMultiConditionFilter(setCurrentFilters, currentFiltersRef), []);

  const defaultColumn: any = useMemo(
    () => ({
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
        />
      ),
    }),
    [],
  );

  //handle leaf column filters
  const assignFilterToLeafColumns = (cols: any[], filterFn: any): any[] => {
    return cols.map((col) => {
      if (col.columns && col.columns.length > 0) {
        return {
          ...col,
          columns: assignFilterToLeafColumns(col.columns, filterFn),
        };
      } else {
        return {
          ...col,
          filter: col.filter || filterFn,
        };
      }
    });
  };

  const tableColumns = useMemo(() => {
    return assignFilterToLeafColumns(columns, multiConditionFilter);
  }, [columns, multiConditionFilter]);

  const tableInstance = useTable<FrequencyChangeData>(
    {
      columns: tableColumns,
      data: tableData,
      defaultColumn,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setGlobalFilter, setAllFilters } =
    tableInstance as TableInstance<FrequencyChangeData> & {
      setGlobalFilter: (filterValue: string | undefined) => void;
      setAllFilters: (filters: any[]) => void;
    };

  const clearFilterForColumn = (columnId: string) => {
    const remainingFilters = currentFiltersRef.current.filter((filter) => filter.columnId !== columnId);
    setCurrentFilters(remainingFilters);

    if (remainingFilters.length === 0) {
      setAllFilters([]);
    } else {
      setAllFilters(
        remainingFilters.map((filter) => ({
          id: filter.columnId,
          value: filter.filterValue,
        })),
      );
    }
  };

  useEffect(() => {
    setGlobalFilter(searchTerm);
  }, [searchTerm, setGlobalFilter]);

  return (
    <section className='table-container-wrapper'>
      <div
        className='table-container recommendations-table-container'
        style={rows.length > 0 ? { height: 'unset', maxHeight: '450px' } : {}}
      >
        <table {...getTableProps()} className='data-table'>
          <thead>
            {headerGroups.map((headerGroup: HeaderGroup<FrequencyChangeData>, groupIdx: number) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                className={groupIdx === 0 ? 'recommendations-grouped-header-row' : ''}
              >
                {headerGroup.headers.map((col) => {
                  const column = col as unknown as ColumnInstance<FrequencyChangeData> &
                    UseSortByColumnProps<FrequencyChangeData> as any;
                  return (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className={`h-[44px] pt-[16px] pb-[16px] pl-[12px] pr-[12px] border-t border-[#4a5463] text-left ${column.id}-column`}
                      style={{
                        ...(column.columns && column.columns.length > 0
                          ? {
                              borderLeft: '2px solid #5d6675',
                              borderRight: '2px solid #5d6675',
                            }
                          : {}),
                      }}
                    >
                      <div
                        className='flex gap-[4px] items-center frequency-table-header'
                        style={{
                          ...(column.columns && column.columns.length > 0
                            ? {
                                justifyContent: 'center',
                                width: '100%',
                              }
                            : {}),
                        }}
                      >
                        <span className='text-[12px] font-semibold text-[#f7f9f9]'>
                          {typeof column.render('Header') === 'string'
                            ? Helpers.toSentenceCase(column.render('Header'))
                            : column.render('Header')}
                        </span>
                        {column.isSorted && (
                          <span
                            className={`freq-sort-icon ${
                              column.isSorted ? (column.isSortedDesc ? 'desc' : 'asc') : 'default'
                            }`}
                            style={{ display: column.render('Header') ? 'inline-block' : 'none' }}
                          />
                        )}
                        {column.canFilter && !column.columns ? <span>{column.render('Filter')}</span> : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  className={onRowClick ? 'cursor-pointer hover:bg-gray-700' : ''}
                  onClick={() => onRowClick && onRowClick(row.original)}
                >
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} className='text-left p-3' style={{ textAlign: 'left' }}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
            {/* Totals row */}
            {rows.length > 0 && Object.keys(totals).length > 0 && (
              <tr className='sticky bottom-0'>
                {headerGroups[headerGroups.length - 1].headers.map((col) => {
                  const column = col as any;
                  let cellContent = '';

                  // Check if this column should show a total
                  if (totals[column.id] || totals[column.accessor]) {
                    const totalValue = totals[column.id] || totals[column.accessor];
                    cellContent = `Total: ${totalValue}`;
                  }

                  return (
                    <td key={column.id} className='bg-[#131f2e]'>
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className='no-matching-records-container text-center p-4 pl-6 pr-6 h-[250px] flex items-center justify-center'>
            <span className='no-matching-records-message text-[#f7f9f9]'>No records available</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default RecommendationsTable;
